import {
  CircularProgress,
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { customSort } from "common/utilities";
import { SortState } from "hooks/useSortableList";
import { SupportedUserDataTypes } from "hooks/useUserDataKeys";
import React, { useMemo, useState } from "react";

export interface OrganizationResourceColumn<T> {
  id: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: string | number;
  disableSort?: boolean;
  sortType?: SupportedUserDataTypes;
  sortValue?: (row: T) => string | number;
  render: (row: T) => React.ReactNode;
}

interface OrganizationResourceTableProps<T> {
  id?: string;
  rows: T[];
  columns: OrganizationResourceColumn<T>[];
  getRowKey: (row: T) => React.Key;
  getRowId?: (row: T) => string;
  loading?: boolean;
  isSelected?: (row: T) => boolean;
  canHover?: (row: T) => boolean;
  onRowClick?: (row: T) => void;
  getLastRowRef?: (row: T, index: number) => ((node: any) => void) | undefined;
}

const OrganizationResourceTable = <T,>({
  id,
  rows,
  columns,
  getRowKey,
  getRowId,
  loading,
  isSelected,
  canHover,
  onRowClick,
  getLastRowRef,
}: OrganizationResourceTableProps<T>) => {
  const [sort, setSort] = useState<Record<string, SortState>>({});

  const handleChangeSort = (key: string) => {
    setSort((current) => {
      switch (current[key]) {
        case undefined:
        case SortState.None:
          return { [key]: SortState.Desc };
        case SortState.Asc:
          return { [key]: SortState.None };
        case SortState.Desc:
          return { [key]: SortState.Asc };
      }
    });
  };

  const sortedRows = useMemo(() => {
    const activeColumn = columns.find(
      (column) => sort[column.id] && sort[column.id] !== SortState.None
    );

    if (!activeColumn) {
      return rows;
    }

    return [...rows].sort((a, b) => {
      const valueA = activeColumn.sortValue
        ? activeColumn.sortValue(a)
        : ((a as any)[activeColumn.id] ?? "");
      const valueB = activeColumn.sortValue
        ? activeColumn.sortValue(b)
        : ((b as any)[activeColumn.id] ?? "");
      const sortType = activeColumn.sortType || SupportedUserDataTypes.string;

      return sort[activeColumn.id] === SortState.Asc
        ? customSort(valueA, valueB, sortType)
        : customSort(valueB, valueA, sortType);
    });
  }, [columns, rows, sort]);

  const renderColumnLabel = (column: OrganizationResourceColumn<T>) => {
    if (column.disableSort || column.id === "actions" || column.id === "status") {
      return (
        <Box component="span" sx={{ fontWeight: "bold" }}>
          {column.label}
        </Box>
      );
    }

    return (
      <Stack flexDirection="row" spacing={1} alignItems="center">
        <Box component="span" sx={{ fontWeight: "bold" }}>
          {column.label}
        </Box>
        <IconButton
          sx={{ opacity: sort[column.id] ? 1 : 0.3, mt: "0px !important" }}
          size="small"
          onClick={() => handleChangeSort(column.id)}
        >
          {sort[column.id] === SortState.Asc ? (
            <ArrowUpward fontSize="small" />
          ) : (
            <ArrowDownward fontSize="small" />
          )}
        </IconButton>
      </Stack>
    );
  };

  return (
    <TableContainer sx={{ height: "100%", overflow: "auto" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align}
                sx={{
                  width: column.width,
                  color: "text.secondary",
                  fontWeight: 800,
                  letterSpacing: 1,
                }}
              >
                {renderColumnLabel(column)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody id={id}>
          {sortedRows.map((row, index) => {
            const hover = canHover ? canHover(row) : Boolean(onRowClick);
            return (
              <TableRow
                hover={hover}
                key={getRowKey(row)}
                id={getRowId?.(row)}
                ref={getLastRowRef?.(row, index)}
                selected={isSelected?.(row)}
                sx={{ cursor: hover ? "pointer" : "default" }}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align}>
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
          {loading && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Stack alignItems="center">
                  <CircularProgress />
                </Stack>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface OrganizationResourceCardsProps<T> extends OrganizationResourceTableProps<T> {}

export const OrganizationResourceCards = <T,>({
  id,
  rows,
  columns,
  getRowKey,
  getRowId,
  loading,
  isSelected,
  canHover,
  onRowClick,
  getLastRowRef,
}: OrganizationResourceCardsProps<T>) => {
  const actionColumn = columns.find((column) => column.id === "actions");
  const contentColumns = columns.filter((column) => column.id !== "actions");
  const primaryColumn = contentColumns[0];
  const detailColumns = contentColumns.slice(1);

  return (
    <Box id={id} sx={{ height: "100%", overflow: "auto" }}>
      {rows.map((row, index) => {
        const hover = canHover ? canHover(row) : Boolean(onRowClick);
        return (
          <Paper
            key={getRowKey(row)}
            id={getRowId?.(row)}
            ref={getLastRowRef?.(row, index)}
            elevation={4}
            onClick={() => onRowClick?.(row)}
            sx={{
              p: 1.5,
              mb: index === rows.length - 1 ? 0 : 1.5,
              borderRadius: 2,
              border: "1.5px solid",
              borderColor: "rgba(0, 0, 0, 0.18)",
              cursor: hover ? "pointer" : "default",
              bgcolor: isSelected?.(row) ? "action.selected" : "background.paper",
              boxShadow: "0 3px 10px rgba(0, 0, 0, 0.14)",
            }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {primaryColumn?.render(row)}
                </Box>
                {actionColumn && (
                  <Box sx={{ flexShrink: 0 }}>
                    {actionColumn.render(row)}
                  </Box>
                )}
              </Stack>
              {detailColumns.length > 0 && <Divider />}
              {detailColumns.map((column) => (
                <Stack key={column.id} direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                  <Box sx={{ minWidth: 96, color: "text.secondary", fontSize: 12, fontWeight: 700 }}>
                    {column.label}
                  </Box>
                  <Box sx={{ minWidth: 0, display: "flex", justifyContent: "flex-end", textAlign: "right" }}>
                    {column.render(row)}
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Paper>
        );
      })}
      {loading && (
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress />
        </Stack>
      )}
    </Box>
  );
};

export default OrganizationResourceTable;
